import { useLayoutEffect, useRef, useState } from 'react';
import { Button, PanoramaViewer } from '@citizen-walk/shared-ui';
import type { StreetViewPin } from '@citizen-walk/shared-types';
import { STREET_SURVEY_DIRECTION_LABELS } from '@citizen-walk/shared-types';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import { useFetchStreetViewImages } from './useFetchStreetViewImages';
import { useStoragePhotoUrl } from './useStoragePhotoUrl';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import {
  boxToRect,
  resolvePinColor,
  resolvePinDetail,
  resolvePinRoleColor,
  resolvePinRoleEmoji,
} from './streetSurveyPinDisplay';

const TOOLTIP_MARGIN = 4;
const TOOLTIP_GAP = 10;
/** 他ピンとの重なり判定に使う概算半径 (px、番号ピンの見た目サイズ28〜34pxの半分 + 少し余裕)。 */
const PIN_AVOID_RADIUS = 20;

/** 矩形と円が重なっているか (吹き出し候補位置が他ピンに被らないかの判定、docs/25 §4-9)。 */
function rectOverlapsCircle(
  rect: { left: number; top: number; width: number; height: number },
  cx: number,
  cy: number,
  r: number,
): boolean {
  const closestX = Math.max(rect.left, Math.min(cx, rect.left + rect.width));
  const closestY = Math.max(rect.top, Math.min(cy, rect.top + rect.height));
  const dx = cx - closestX;
  const dy = cy - closestY;
  return dx * dx + dy * dy < r * r;
}

/**
 * 検出要素の「エリア表示」モード (docs/25c、2026-07-13 改訂)。
 * - both: 番号ピンは常時表示、エリアは**フォーカス中の要素のみ**表示 (既定)。
 * - area: 全要素のエリアを常時表示 (密なシーンの俯瞰用)。
 * - pin : エリアを一切表示しない (従来の見た目)。
 */
type PinDisplayMode = 'both' | 'area' | 'pin';
// box2d は矩形だが、実際の対象物は矩形ぴったりではないことが多いため、楕円形の放射グラデーション
// (中心が濃く周辺は透明にフェード) で「輪郭のあいまいな、なめらかなエリア」を表現する
// (docs/25c Candidate A)。'area' モードで多数の要素が重なる密なシーンでも、フェードのおかげで
// 単色飽和しにくい (矩形べた塗りで一度試して飽和したため、既定の濃さは控えめに設定)。
// 2026-07-14: multiply合成だと暗い背景 (アスファルト・影) で色がほぼ消えて視認できないとの
// フィードバックを受け、合成モードを normal に変更した上で不透明度も引き上げ。
const AREA_OPACITY_NORMAL = 0.35;
const AREA_OPACITY_FOCUSED = 0.72;
const AREA_OPACITY_DIMMED = 0.12;

/** 方向の表示名 (shared-types の定義を使う、docs/98 R-98 低)。 */
const DIRECTION_LABELS = STREET_SURVEY_DIRECTION_LABELS;
const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

function compassLabel(deg: number): string {
  const normalized = ((deg % 360) + 360) % 360;
  const index = Math.round(normalized / 45) % 8;
  return COMPASS[index] ?? 'N';
}

/** エリア表示 (該当モード or フォーカス時のみ) の要否を判定する。 */
function shouldShowArea(mode: PinDisplayMode, rank: number, focusRank: number | null): boolean {
  if (mode === 'pin') return false;
  if (mode === 'area') return true;
  return rank === focusRank; // 'both': フォーカス中の要素のみ
}

/** エリアの不透明度 (モードとフォーカス状態から解決)。'both' はフォーカス時のみ描画されるため常に強調値。 */
function resolveAreaOpacity(mode: PinDisplayMode, rank: number, focusRank: number | null): number {
  if (mode !== 'area') return AREA_OPACITY_FOCUSED;
  if (focusRank === null) return AREA_OPACITY_NORMAL;
  return rank === focusRank ? AREA_OPACITY_FOCUSED : AREA_OPACITY_DIMMED;
}

/**
 * エリアの合成モード。単体表示 ('both' のフォーカス時、'area' のフォーカス要素) は背景に
 * 埋もれず濃く見せたいので normal 合成、'area' モードで同時に多数重なる非フォーカス要素は
 * normal だと重なりが白飛び/単色飽和するため multiply で自然になじませる
 * (2026-07-14: 視認性改善のため normal を導入した際、'area' モードの同時多重表示で
 * 以前解決した「飽和して見えなくなる」問題がぶり返したための使い分け)。
 */
function resolveAreaBlendMode(
  mode: PinDisplayMode,
  rank: number,
  focusRank: number | null,
): 'normal' | 'multiply' {
  if (mode !== 'area') return 'normal';
  return rank === focusRank ? 'normal' : 'multiply';
}

const AREA_SCALE_MIN = 0.6;
const AREA_SCALE_MAX = 1.4;
/** この距離 (m) 以下は最大サイズに張り付く。 */
const AREA_SCALE_NEAR_M = 5;
/** この距離 (m) 以上は最小サイズに張り付く。 */
const AREA_SCALE_FAR_M = 40;

/**
 * 撮影地点からの推定距離 (`distanceMeters`) に応じてエリアの見た目サイズを補正する
 * (docs/25c 2026-07-13 追加: 手前を大きく・奥を小さく、遠近感を反映)。ホバー判定領域
 * (box2d 実寸) には影響させず、視覚的なエリア表示のみ `transform: scale()` で伸縮する。
 */
function resolveAreaScale(distanceMeters: number): number {
  const t = Math.min(
    1,
    Math.max(0, (distanceMeters - AREA_SCALE_NEAR_M) / (AREA_SCALE_FAR_M - AREA_SCALE_NEAR_M)),
  );
  return AREA_SCALE_MAX - t * (AREA_SCALE_MAX - AREA_SCALE_MIN);
}

export interface StreetSurveyImageBoardProps {
  districtId: string;
  post: StreetSurveyPost;
  primaryAxis: PrimaryAxisResolution;
  roleTypeAxis?: RoleTypeAxisResolution;
  highlightedRank?: number | null;
  /** M3 outputFormat.schema.reasoningField (mode ごとにキー名が異なるため決め打ちしない、2026-07-14)。
   *  `enableHoverTooltip` が true のときのみ使用する。 */
  reasoningField?: string;
  /**
   * ピンホバー時に検出内容の黒い吹き出しを表示するか (既定 false)。
   * 密なシーン (`StreetSurveyPage.tsx` の地図横パネル) では吹き出しが他ピンと重なって読みにくい
   * というユーザー指摘を受け既定で無効化した (2026-07-21)。一方「詳細レポート」永続ページ
   * (`StreetSurveyDetailPage.tsx`) では読み物として吹き出し表示を残したい要望があり、
   * 明示的に true を渡した画面でのみ有効にする。
   */
  enableHoverTooltip?: boolean;
}

export function StreetSurveyImageBoard({
  districtId,
  post,
  primaryAxis,
  roleTypeAxis,
  highlightedRank,
  reasoningField,
  enableHoverTooltip = false,
}: StreetSurveyImageBoardProps) {
  const streetView = post.streetView;
  const headings = streetView?.imageParams.headings ?? [];
  // docs/27 §6-2: 登録済み 360度写真は Google から取得せず、全天球画像をブラウザで同じ 4 方向に
  // 固定視点描画する (サーバ側の切り出しと同じ規約なので box2d の位置がそのまま合う)。
  const isUpload360 = post.provider === 'upload-360';
  const fetchTarget = !isUpload360 && streetView?.panoId ? { panoId: streetView.panoId } : null;
  const imagesState = useFetchStreetViewImages(districtId, fetchTarget, headings);
  const uploadPhoto = useStoragePhotoUrl(
    isUpload360 ? post.photoPath : null,
    isUpload360 ? post.photoUrl : null,
  );
  const [hoveredRank, setHoveredRank] = useState<number | null>(null);
  const [displayMode, setDisplayMode] = useState<PinDisplayMode>('both');
  // ぼんやりエリアの強調対象: マウスホバー中はそれを優先、無ければリスト選択 (highlightedRank)
  // を継続表示する (docs/25c §5 双方向連動)。
  const focusRank = hoveredRank ?? highlightedRank ?? null;
  const containerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null);

  const hoveredPin = streetView?.pins.find((p) => p.rank === hoveredRank) ?? null;
  const hoveredDetail =
    enableHoverTooltip && hoveredPin
      ? resolvePinDetail(post, primaryAxis, hoveredPin.rank, reasoningField)
      : null;

  // 吹き出しの実測サイズをもとに、タイル (写真) の範囲内に収まりつつ、他の検出要素ピンに
  // なるべく被らない位置を選ぶ (docs/25 §4-9、2026-07-13: 密なシーンで吹き出しが他ピンを
  // 隠してしまう報告への対応)。上/下/右/左の4候補を作り、コンテナ内にクランプした上で、
  // 他ピンとの重なり数が最小の候補を採用する (同数なら上→下→右→左の優先順で選ぶ)。
  useLayoutEffect(() => {
    if (!enableHoverTooltip || !hoveredPin || !hoveredDetail) {
      setTooltipPos(null);
      return;
    }
    const container = containerRefs.current[hoveredPin.imageIndex];
    const tooltipEl = tooltipRef.current;
    if (!container || !tooltipEl) {
      setTooltipPos(null);
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const tw = tooltipRect.width;
    const th = tooltipRect.height;
    const pinX = ((hoveredPin.box2d[1] + hoveredPin.box2d[3]) / 2 / 1000) * containerRect.width;
    const pinY = ((hoveredPin.box2d[0] + hoveredPin.box2d[2]) / 2 / 1000) * containerRect.height;

    const clampPos = (left: number, top: number) => ({
      left: Math.max(TOOLTIP_MARGIN, Math.min(left, containerRect.width - tw - TOOLTIP_MARGIN)),
      top: Math.max(TOOLTIP_MARGIN, Math.min(top, containerRect.height - th - TOOLTIP_MARGIN)),
    });
    // 4候補とも「ピン中心から最低でも見た目半径ぶんは離す」よう -14 を揃える (2026-07-21修正:
    // 「上」候補だけこの余白が抜けており、ホバー中のピン自体に吹き出しが被る報告があった)。
    const candidates = [
      clampPos(pinX - tw / 2, pinY - th - TOOLTIP_GAP - 14), // 上
      clampPos(pinX - tw / 2, pinY + TOOLTIP_GAP + 14), // 下
      clampPos(pinX + TOOLTIP_GAP + 14, pinY - th / 2), // 右
      clampPos(pinX - tw - TOOLTIP_GAP - 14, pinY - th / 2), // 左
    ];

    const otherPins = (streetView?.pins ?? []).filter(
      (p) => p.imageIndex === hoveredPin.imageIndex && p.rank !== hoveredPin.rank,
    );
    // ホバー中のピン自身も重なり回避の対象に含める (2026-07-21修正: 従来は他ピンのみが対象で、
    // コンテナ端でのクランプにより候補が全てホバー中のピンへ寄ってしまうケースを防げなかった)。
    const avoidCenters = [hoveredPin, ...otherPins].map((p) => ({
      cx: ((p.box2d[1] + p.box2d[3]) / 2 / 1000) * containerRect.width,
      cy: ((p.box2d[0] + p.box2d[2]) / 2 / 1000) * containerRect.height,
    }));
    const overlapCount = (rect: { left: number; top: number }) =>
      avoidCenters.filter(({ cx, cy }) =>
        rectOverlapsCircle(
          { left: rect.left, top: rect.top, width: tw, height: th },
          cx,
          cy,
          PIN_AVOID_RADIUS,
        ),
      ).length;

    let best = candidates[0]!;
    let bestOverlap = overlapCount(best);
    for (let i = 1; i < candidates.length; i++) {
      const c = candidates[i]!;
      const overlap = overlapCount(c);
      if (overlap < bestOverlap) {
        best = c;
        bestOverlap = overlap;
        if (bestOverlap === 0) break; // 完全に被らない候補が見つかれば探索終了
      }
    }

    setTooltipPos(best);
    // hoveredPin/hoveredDetail は毎レンダー新規生成されるため依存に含めない (無限ループ防止)。
    // 実際に位置を再計算すべきタイミングは hoveredRank/enableHoverTooltip (プリミティブ) が
    // 変わったときだけ。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredRank, enableHoverTooltip]);

  if (!streetView) return null;

  // streetView.pins (座標抽出) は生成AIの生レスポンスから抽出するため、M3 の分類配列
  // maxItems (例: bousai-kids-v4-format は 6件) で切り詰められる前の全検出要素数を持つ。
  // 一方 post.classifications は切り詰め後のデータのため、両者の件数がズレることがある
  // (2026-07-14: 座標はあるが対応する分類データが無いピンがグレー表示され続けていた不具合)。
  // 分類データの無いランクは表示しても色・tooltip とも空になるため描画しない。
  const classifiedRanks = new Set(post.classifications.map((c) => c.rank));
  const pinsByImage = (index: number): StreetViewPin[] =>
    streetView.pins.filter((p) => p.imageIndex === index && classifiedRanks.has(p.rank));

  // ピンの色は地図の凡例 (色分けする調査項目、机上調査の設定で選ぶ) と一致させる — 色分けの項目が無いときは
  // 集計の調査項目の色にフォールバックする (docs/25 §3 中核原則、機構は特定の項目
  // の有無に依存しない)。
  const pinColor = (rank: number) =>
    (roleTypeAxis ? resolvePinRoleColor(post, roleTypeAxis, rank) : undefined) ??
    resolvePinColor(post, primaryAxis, rank);
  const pinEmoji = (rank: number) =>
    roleTypeAxis ? resolvePinRoleEmoji(post, roleTypeAxis, rank) : undefined;

  return (
    <div>
      {/* 検出要素の位置表示モード切替 (docs/25c §5、密なシーンで番号ピン/ぼんやり領域を選べる)。 */}
      <div className="flex items-center gap-1 mb-2">
        <span className="text-xs text-slate-500 mr-1">表示:</span>
        <Button
          size="sm"
          variant={displayMode === 'both' ? 'primary' : 'secondary'}
          onClick={() => setDisplayMode('both')}
        >
          両方
        </Button>
        <Button
          size="sm"
          variant={displayMode === 'area' ? 'primary' : 'secondary'}
          onClick={() => setDisplayMode('area')}
        >
          領域のみ
        </Button>
        <Button
          size="sm"
          variant={displayMode === 'pin' ? 'primary' : 'secondary'}
          onClick={() => setDisplayMode('pin')}
        >
          ピンのみ
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {DIRECTION_LABELS.map((label, i) => {
          const heading = headings[i] ?? 0;
          const image = imagesState.status === 'ready' ? imagesState.images[i] : undefined;
          const tileLoading = isUpload360
            ? uploadPhoto.status === 'loading' || uploadPhoto.status === 'idle'
            : imagesState.status === 'loading';
          const tileError = isUpload360
            ? uploadPhoto.status === 'error'
              ? uploadPhoto.error
              : null
            : imagesState.status === 'error'
              ? imagesState.error
              : null;
          const tileReady = isUpload360 ? uploadPhoto.status === 'ready' : !!image;
          return (
            <div key={label} className="relative rounded-lg border border-slate-200 bg-slate-100">
              <div className="absolute top-1 left-1 z-10 bg-black/60 text-white text-[11px] px-1.5 py-0.5 rounded">
                {label} ({heading}° {compassLabel(heading)})
              </div>
              {tileLoading && (
                <div className="aspect-[4/3] overflow-hidden rounded-lg flex items-center justify-center text-xs text-slate-400">
                  読込中…
                </div>
              )}
              {tileError && (
                <div className="aspect-[4/3] overflow-hidden rounded-lg flex items-center justify-center text-xs text-danger-600 p-2 text-center">
                  画像取得に失敗しました: {tileError}
                </div>
              )}
              {tileReady && (
                <div
                  // isolate: 番号ピン (zIndex 1000/1001、エリアより手前にする都合上の大きな値) が
                  // このタイルの外の要素 (モーダル z-50 等) より前面に出てしまわないよう、新しい
                  // スタッキングコンテキストでタイル内に閉じ込める (2026-07-15、報告された「画像外でも
                  // ピンが最前面に出る」不具合の修正)。
                  className="relative aspect-[4/3] overflow-hidden rounded-lg isolate"
                  ref={(el) => {
                    containerRefs.current[i] = el;
                  }}
                >
                  {isUpload360 && uploadPhoto.status === 'ready' ? (
                    <PanoramaViewer
                      src={uploadPhoto.url}
                      baseHeadingDeg={streetView.baseHeading}
                      pov={{
                        heading,
                        pitch: streetView.imageParams.pitch,
                        hfov: streetView.imageParams.fov,
                      }}
                      interactive={false}
                      showCompass={false}
                      showControls={false}
                      enableFullscreen={false}
                      className="w-full h-full"
                      alt={`${label} ${heading}°`}
                    />
                  ) : image ? (
                    <img
                      src={`data:${image.mimeType};base64,${image.data}`}
                      alt={`${label} ${heading}°`}
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                  {/* Pass 1: エリア表示レイヤ (視覚のみ)。番号ピンより必ず先に描画し、'area' モード
                    (ピン非表示) 以外では pointer-events を持たせない — ピンの大きな box2d が
                    他要素のホバーを奪ってしまう不具合を避けるため、ホバー判定は Pass 2 の
                    番号ピン (常にエリアより手前・小さく正確な位置) に一本化する (docs/25c 2026-07-13)。 */}
                  {pinsByImage(i).map((pin) => {
                    const showArea = shouldShowArea(displayMode, pin.rank, focusRank);
                    if (!showArea) return null;
                    const rect = boxToRect(pin.box2d);
                    const isFocused = pin.rank === focusRank;
                    const color = pinColor(pin.rank);
                    const areaOpacity = resolveAreaOpacity(displayMode, pin.rank, focusRank);
                    const areaBlendMode = resolveAreaBlendMode(displayMode, pin.rank, focusRank);
                    const areaScale = resolveAreaScale(pin.distanceMeters);
                    // 'area' モードはピン自体が無いため、エリアがホバー判定を引き受ける。
                    const areaInteractive = displayMode === 'area';
                    return (
                      <div
                        key={`area-${pin.rank}`}
                        aria-hidden={!areaInteractive}
                        className="absolute rounded-full"
                        onMouseEnter={areaInteractive ? () => setHoveredRank(pin.rank) : undefined}
                        onMouseLeave={areaInteractive ? () => setHoveredRank(null) : undefined}
                        style={{
                          left: `${rect.left}%`,
                          top: `${rect.top}%`,
                          width: `${rect.width}%`,
                          height: `${rect.height}%`,
                          background: `radial-gradient(ellipse at center, ${color} 0%, ${color} 45%, transparent 85%)`,
                          opacity: areaOpacity,
                          transform: `scale(${areaScale})`,
                          mixBlendMode: areaBlendMode,
                          transition: 'opacity 0.15s, transform 0.15s',
                          pointerEvents: areaInteractive ? 'auto' : 'none',
                          cursor: areaInteractive ? 'default' : undefined,
                          // 近い要素 (distanceMeters 小) を手前に。番号ピン (Pass 2, zIndex>=1000) は
                          // 常にこれより上に来る (docs/25c §4)。
                          zIndex: isFocused
                            ? 500
                            : Math.max(1, 100 - Math.round(pin.distanceMeters)),
                        }}
                      />
                    );
                  })}
                  {/* Pass 2: 番号ピンレイヤ。box2d の中心に固定サイズで描画し、常にエリアより手前・
                    正確な位置でホバー判定する ('pin'/'both' モードで表示、'area' モードでは非表示)。 */}
                  {displayMode !== 'area' &&
                    pinsByImage(i).map((pin) => {
                      const rect = boxToRect(pin.box2d);
                      const left = rect.left + rect.width / 2;
                      const top = rect.top + rect.height / 2;
                      const isHighlighted = pin.rank === highlightedRank;
                      const isFocused = pin.rank === focusRank;
                      const emoji = pinEmoji(pin.rank);
                      const size = isHighlighted ? 34 : 28;
                      const color = pinColor(pin.rank);
                      return (
                        <div
                          key={`pin-${pin.rank}`}
                          aria-label={`${pin.rank}`}
                          className="absolute flex items-center justify-center rounded-full text-white -translate-x-1/2 -translate-y-1/2 cursor-default"
                          onMouseEnter={() => setHoveredRank(pin.rank)}
                          onMouseLeave={() => setHoveredRank(null)}
                          style={{
                            left: `${left}%`,
                            top: `${top}%`,
                            width: size,
                            height: size,
                            fontSize: emoji ? size * 0.65 : size * 0.5,
                            backgroundColor: color,
                            border: isHighlighted ? '2px solid #0EA5E9' : '2px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
                            transition: 'width 0.15s, height 0.15s',
                            zIndex: isFocused ? 1001 : 1000,
                          }}
                        >
                          {/* 色分けする調査項目の絵文字が解決できればそれをメイン表示 (docs/25 §3-1)、無ければ順位の番号 */}
                          {emoji ? (
                            <span aria-hidden="true" className="leading-none">
                              {emoji}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold leading-none">{pin.rank}</span>
                          )}
                          {/* ランクバッジはピンの右上・完全に外側に配置し、絵文字/色を隠さないようにする。 */}
                          {emoji && (
                            <span className="absolute -top-2 -right-2 bg-slate-900 border border-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold leading-none shadow">
                              {pin.rank}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  {/* 吹き出しはタイル (座標系はコンテナ基準) の直接の子として1つだけ描画し、
                    実測サイズでタイル内に収まる位置へクランプする (docs/25 §4-9)。
                    `enableHoverTooltip` が true の画面 (詳細レポート) のみ表示する。 */}
                  {enableHoverTooltip && hoveredPin?.imageIndex === i && hoveredDetail && (
                    <div
                      ref={tooltipRef}
                      className="absolute z-20 w-56 rounded-lg bg-black/85 text-white text-xs p-2.5 shadow-lg pointer-events-none"
                      style={
                        tooltipPos
                          ? { left: tooltipPos.left, top: tooltipPos.top }
                          : { left: 0, top: 0, visibility: 'hidden' }
                      }
                    >
                      <p className="font-semibold text-[13px] leading-snug">
                        {hoveredDetail.title}
                      </p>
                      {hoveredDetail.subtitle && (
                        <p className="text-slate-300 text-[11px] mt-0.5">
                          {hoveredDetail.subtitle}
                        </p>
                      )}
                      <p className="mt-1 leading-snug">{hoveredDetail.body}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
